import { useEffect, useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { Button, ConfirmButton } from 'components/common';
import { Box } from 'components/common/box';
import {
  Form,
  FormGroup,
  FormSection,
  WorkloadSettings
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import {
  SettingsCompletionCertificate,
  SettingsCompletionEnable,
  SettingsCompletionExpiration,
  SettingsCompletionForm
} from '.';

import type { FormApi } from 'informed';
import type { Course } from 'shared-types';

export type CourseCompletionSettings = Pick<
  Course,
  | 'automaticCompletion'
  | 'certificateAllowDownloading'
  | 'certificateBackgroundImage'
  | 'certificateCustomContent'
  | 'certificateSendToUser'
  | 'certificateSendToOthers'
  | 'certificateShowExpirationDate'
  | 'certificateShowScore'
  | 'certificateOtherEmails'
  | 'certificateLanguage'
  | 'completionAllowAnswersAfterFinishing'
  | 'completionAllowRetries'
  | 'completionElectronicSignature'
  | 'completionEmailOthersShowCustomContent'
  | 'completionEmailShowCustomContent'
  | 'completionEnabled'
  | 'completionExpiration'
  | 'completionExpirationValue'
  | 'completionExpirationReminderIntervals'
  | 'completionExpirationReminders'
  | 'completionExpirationRemindersOthers'
  | 'completionExpirationRemindersEmailList'
  | 'completionFormCustomInstructions'
  | 'completionFormShowCustomContent'
  | 'completionTasks'
  | 'completionReading'
  | 'completionVideoMaxPlaybackSpeed'
  | 'completionVideos'
  | 'completionWorkload'
  | 'completionWorkloadValue'
> & { CertificateTemplateId: number };

const SettingsCompletion = () => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig: {
      courseCompletionAttributeConditions,
      courseCompletionRequireExpiration,
      courseCompletionRequireWorkload
    }
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);
  const certificateBackgroundImageUploader = useRef<any>(undefined);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (formApi.current) {
      const requireExpiration =
        courseCompletionRequireExpiration ||
        courseCompletionAttributeConditions.some((condition) => {
          return (
            condition.requireExpiration &&
            course.customAttributes?.[condition.key] === condition.value
          );
        });
      if (formApi.current && requireExpiration)
        formApi.current.setValue('completionExpiration', true);

      const requireWorkload =
        courseCompletionRequireWorkload ||
        courseCompletionAttributeConditions.some((condition) => {
          return (
            condition.requireWorkload &&
            course.customAttributes?.[condition.key] === condition.value
          );
        });
      if (formApi.current && requireWorkload)
        formApi.current.setValue('completionWorkload', true);
    }
  }, []);

  const handleConfirm = async () => {
    const { values } = formApi.current.getState();
    await handleSubmit(values as typeof initialValues);

    return true;
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setLoading(true);

    try {
      if (certificateBackgroundImageUploader?.current?.state.file?.id) {
        const result =
          await certificateBackgroundImageUploader.current.upload();
        if (result) values.certificateBackgroundImage = result.url;
      } else if (
        course.certificateBackgroundImage &&
        !certificateBackgroundImageUploader?.current?.state.initialFilename
      ) {
        values.certificateBackgroundImage = null;
      }

      if (!values.CertificateTemplateId) values.CertificateTemplateId = null;

      await api.put(`/admin/courses/${course.id}`, values);

      const courses = await api.get(`/admin/courses?id=${course.id}`);

      setLoading(false);
      dispatch.updatePageState({
        course: courses.data.courses[0],
        hasUnsavedChanges: false
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      setLoading(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const requireExpiration =
    courseCompletionRequireExpiration ||
    courseCompletionAttributeConditions.some((condition) => {
      return (
        condition.requireExpiration &&
        course.customAttributes?.[condition.key] === condition.value
      );
    });

  const requireWorkload =
    courseCompletionRequireWorkload ||
    courseCompletionAttributeConditions.some((condition) => {
      return (
        condition.requireWorkload &&
        course.customAttributes?.[condition.key] === condition.value
      );
    });

  const initialValues = {
    automaticCompletion: course.automaticCompletion,
    certificateAllowDownloading: course.certificateAllowDownloading,
    certificateBackgroundImage: course.certificateBackgroundImage,
    certificateCustomContent: course.certificateCustomContent,
    certificateSendToUser: course.certificateSendToUser,
    certificateSendToOthers: course.certificateSendToOthers,
    certificateShowExpirationDate: course.certificateShowExpirationDate,
    certificateShowScore: course.certificateShowScore,
    certificateOtherEmails: course.certificateOtherEmails,
    certificateLanguage: course.certificateLanguage,
    CertificateTemplateId: course.certificateTemplateId || 0,
    completionAllowAnswersAfterFinishing:
      course.completionAllowAnswersAfterFinishing,
    completionAllowRetries: course.completionAllowRetries,
    completionElectronicSignature: course.completionElectronicSignature,
    completionEmailOthersShowCustomContent:
      course.completionEmailOthersShowCustomContent,
    completionEmailShowCustomContent: course.completionEmailShowCustomContent,
    completionEnabled: course.completionEnabled,
    completionExpiration: course.completionExpiration,
    completionExpirationValue: course.completionExpirationValue || 12,
    completionExpirationReminderIntervals:
      course.completionExpirationReminderIntervals,
    completionExpirationReminders: course.completionExpirationReminders,
    completionExpirationRemindersOthers:
      course.completionExpirationRemindersOthers,
    completionExpirationRemindersEmailList:
      course.completionExpirationRemindersEmailList,
    completionFormCustomInstructions: {
      ...course.completionFormCustomInstructions,
      contentLocales:
        course.completionFormCustomInstructions?.contentLocales || {},
      buttonTextLocales:
        course.completionFormCustomInstructions?.buttonTextLocales || {},
      titleLocales: course.completionFormCustomInstructions?.titleLocales || {}
    },
    completionFormShowCustomContent: course.completionFormShowCustomContent,
    completionTasks: course.completionTasks,
    completionReading: course.completionReading,
    completionVideoMaxPlaybackSpeed: course.completionVideoMaxPlaybackSpeed,
    completionVideos: course.completionVideos,
    completionWorkload: course.completionWorkload,
    completionWorkloadValue: course.completionWorkloadValue || 1
  };

  return (
    <div className="settings">
      <Box padding={0} title={messages.settingsCompletion}>
        <Form
          allowEmptyStrings={true}
          getApi={(form) => (formApi.current = form)}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ formState }) => (
            <>
              <UnsavedFormChanges
                initialValues={initialValues}
                styles={{ margin: '15px 15px 5px' }}
              />

              <div>
                <SettingsCompletionEnable formState={formState} />

                <WorkloadSettings
                  disabled={
                    !formState.values?.completionEnabled || requireWorkload
                  }
                  fieldWorkloadEnabled="completionWorkload"
                  fieldWorkloadValue="completionWorkloadValue"
                  isFormSection={true}
                  workloadEnabledProps={{
                    label: messages.settingsCompletionWorkloadSet
                  }}
                  workloadValueProps={{
                    label: messages.settingsCompletionWorkloadTime
                  }}
                />

                <SettingsCompletionExpiration
                  requireExpiration={requireExpiration}
                />

                <SettingsCompletionForm course={course} />

                <SettingsCompletionCertificate
                  backgroundImageUploaderRef={
                    certificateBackgroundImageUploader
                  }
                  course={course}
                />

                <FormSection>
                  <FormGroup>
                    {formState.values.completionExpiration !==
                      initialValues.completionExpiration ||
                    formState.values.completionExpirationValue !==
                      initialValues.completionExpirationValue ? (
                      <ConfirmButton
                        actionText={messages.commonSaveChanges}
                        buttonText={messages.commonSaveChanges}
                        category="success"
                        confirmText={
                          messages.settingsCompletionExpirationConfirm
                        }
                        confirmTitle={messages.commonSaveChanges}
                        onConfirm={handleConfirm}
                      />
                    ) : (
                      <Button
                        category="success"
                        disabled={formState.invalid}
                        loading={loading}
                        text={messages.commonSaveChanges}
                        type="submit"
                      />
                    )}
                  </FormGroup>
                </FormSection>
              </div>
            </>
          )}
        </Form>
      </Box>
    </div>
  );
};

export default SettingsCompletion;
